import {Button, Pagination} from "antd";
import React, {useState} from "react";
import {PaginationProps} from "antd/es/pagination/Pagination";
import {getDefaultPageOutput, PageOutput} from "@/base/types/Pagination";
import {Loading} from "@/base/components/Loading";
import {useTranslation} from "next-i18next";
import useMemoryEffect from "@/base/components/util/useMemoryEffect";

export type OnPageChange<T> = (page: number, perPage: number, setList: (list: PageOutput<T>) => void, list?: PageOutput<T>) => Promise<void>;

type Props<T> = {
	state?: [PageOutput<T>, React.Dispatch<React.SetStateAction<PageOutput<T>>>]
	className?: string
	bodyClassName?: string
	dependencies?: any[]
	dependenciesResetPages?: any[]
	defaultLoading?: boolean
	onChange: OnPageChange<T>
	emptyItem?: () => React.ReactNode
	pageProps?: PaginationProps
	renderItem?: (item: T, index: number) => React.ReactNode
	onChanged?: (list: PageOutput<T>) => void
	format?: (items: T[]) => T[]
	preChildren?: React.ReactNode
	ignoreFullData?: boolean
	switchLoading?: boolean
	loadingTwo?: boolean
	innerClassName?: string
	children?: React.ReactNode
	rootClassName?: string
	loadMore?: boolean
}

export function PagedItemsInner<T>(props: Props<T>, ref: React.Ref<HTMLDivElement>) {
	const {t} = useTranslation();

	const [firstLoad, setFirstLoad] = useState(true);

	const ignoreFullData = props.ignoreFullData || false;

	const perPage = props.pageProps?.pageSize || 25;
	const [list, setList] = props.state || useState<PageOutput<T>>(getDefaultPageOutput(perPage));
	const [isLoading, setIsLoading] = useState(props.hasOwnProperty('defaultLoading') ? props.defaultLoading : true);

	const loadMore = props.loadMore || false;

	const format = props.format || ((items: T[]) => items);

	const onChange = (page: number, perPage: number, ignoreFullData_: boolean = false) => {
		if (firstLoad)
		{
			setFirstLoad(false);

			if (list?.data && list?.data?.length > 0)
			{
				console.debug('Using existing data');
				setIsLoading(false);
				return;
			}
		}

		if (!ignoreFullData && !ignoreFullData_ && list.full_data && list.full_data.length > 0)
		{
			console.debug('Using full data', list.full_data, ignoreFullData_, ignoreFullData);
			const newData = list?.full_data[page - 1] || [];
			setList({...list, current_page: page, per_page: perPage, data: newData});
			return;
		}

		setIsLoading(firstLoad || (props.switchLoading != undefined ? props.switchLoading : true));

		props.onChange(page, perPage, setList, list).finally(() => setIsLoading(false));
	}

	useMemoryEffect(() => onChange(list.current_page || 1, list.per_page || perPage), [...props.dependencies || []]);
	useMemoryEffect(() => {
		if (props.dependenciesResetPages && props.dependenciesResetPages.length > 0 && !firstLoad)
		{
			onChange(1, list.per_page || perPage, true);
		}
	}, [...props.dependenciesResetPages || []]);

	useMemoryEffect(() => {
		if (props.onChanged && list && list.data)
		{
			props.onChanged(list);
		}
	}, [list]);

	const onEmptyItem = props.emptyItem || (() => (
		<div className={`text-center text-gray-400 py-6`}>
			<p>{t('base-common:list.empty')}</p>
		</div>
	))

	const hasPages = list?.current_page && list?.last_page && list?.current_page < list?.last_page;

	return <Loading show={isLoading || props?.loadingTwo} className={props.rootClassName || ''} innerClassName={`flex justify-center flex-col space-y-4 ${props.className || ''}`} ref={ref}>
		{list?.data?.length === 0 && list?.total == 0 ?
			onEmptyItem() : (
				<React.Fragment>
					<div className={`w-full ${props.bodyClassName || ''}`}>
						{props.preChildren || null}
						{props.renderItem && format(list?.data || [])?.map((item, index) => <React.Fragment key={index}>
							{props.renderItem && props.renderItem(item, index)}
						</React.Fragment>)}
						{props.children || null}
					</div>
					{loadMore ? (hasPages ? <div>
						<Button
							className={`btn btn-primary text-center w-full`}
							type={'link'}
							onClick={() => onChange((list.current_page || 1) + 1, list.per_page || perPage)}
						>
							{t('base-common:list.load-more')}
						</Button>
					</div> : null) : <Pagination
						className={`text-center ${props.innerClassName || ''}`}
						{...{
							defaultCurrent: list.current_page || 1,
							total: list.total || 1,
							showLessItems: true,
							showSizeChanger: false,
							hideOnSinglePage: true,
							...(props.pageProps || {}),
							pageSize: list.per_page || perPage,
							onChange
						} as PaginationProps}
					/>}
				</React.Fragment>
			)}
	</Loading>
}

export const PagedItems = React.forwardRef(PagedItemsInner) as <T>(props: Props<T> & {
	ref?: React.Ref<HTMLDivElement>
}) => React.ReactElement;
export default PagedItems;